"use client";

import { useState } from "react";
import { SelectInput, TextInput, Toggle } from "@dionatanbig2be/big2be-ui";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors, FieldValues, Path } from "react-hook-form";
import type { AuthFieldDef } from "../lib/auth-fields";

// `cpf-cnpj` e `phone` são máscaras da própria lib (prop `mask` do TextInput).
// E-mail não é máscara: só normalizamos a digitação aqui.
const normalizeEmail = (value: string): string => value.replace(/\s+/g, "").toLowerCase();

interface AuthFieldProps<T extends FieldValues> {
    def: AuthFieldDef;
    control: Control<T>;
    errors: FieldErrors<T>;
    disabled?: boolean;
    /** Esconde o label visualmente (mantém pra leitor de tela) — use quando algo ao lado já nomeia o campo. */
    visuallyHiddenLabel?: boolean;
}

/** Renderiza um campo de auth (big2be-ui) a partir da definição declarativa. */
export function AuthField<T extends FieldValues>({
    def,
    control,
    errors,
    disabled,
    visuallyHiddenLabel,
}: AuthFieldProps<T>) {
    const [showPassword, setShowPassword] = useState(false);
    const error = errors[def.name as Path<T>]?.message as string | undefined;
    const mask = def.mask && def.mask !== "email" ? def.mask : undefined;
    const isPassword = def.type === "password";

    return (
        <Controller
            name={def.name as Path<T>}
            control={control}
            render={({ field: { value, onChange, onBlur, name, ref } }) => {
                if (def.type === "checkbox") {
                    return (
                        <div className="col-span-2">
                            <Toggle
                                tone="primary"
                                size="sm"
                                label={def.label}
                                disabled={disabled}
                                checked={Boolean(value)}
                                onChange={onChange}
                            />
                            {error && <span className="mt-1 block text-xs text-error">{error}</span>}
                        </div>
                    );
                }

                if (def.type === "select") {
                    return (
                        <div className={def.full ? "col-span-2" : "col-span-2 md:col-span-1"}>
                            <SelectInput
                                label={visuallyHiddenLabel ? undefined : def.label}
                                options={def.options ?? []}
                                placeholder={def.placeholder}
                                disabled={disabled}
                                error={error}
                                clearable
                                name={name}
                                value={(value as string) ?? undefined}
                                onChange={(next) => onChange(next ?? "")}
                            />
                        </div>
                    );
                }

                return (
                    <div className={def.full ? "col-span-2" : "col-span-2 md:col-span-1"}>
                        <label
                            className={
                                visuallyHiddenLabel
                                    ? "sr-only"
                                    : "mb-1 block text-sm font-medium"
                            }
                            htmlFor={`auth-${def.name}`}
                        >
                            {def.label}
                        </label>
                        <TextInput
                            id={`auth-${def.name}`}
                            type={isPassword && showPassword ? "text" : (def.type ?? "text")}
                            inputMode={mask ? "numeric" : undefined}
                            mask={mask}
                            placeholder={def.placeholder}
                            autoComplete={def.autoComplete}
                            maxLength={def.maxLength}
                            disabled={disabled}
                            error={error}
                            className="w-full"
                            name={name}
                            ref={ref}
                            value={(value as string) ?? ""}
                            onBlur={onBlur}
                            onChange={(event) =>
                                onChange(
                                    def.mask === "email"
                                        ? normalizeEmail(event.target.value)
                                        : event.target.value
                                )
                            }
                            endAdornment={
                                isPassword ? (
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((visible) => !visible)}
                                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                        className="flex h-full cursor-pointer items-center justify-center text-base-content/60 transition-colors hover:text-base-content"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                ) : undefined
                            }
                        />
                    </div>
                );
            }}
        />
    );
}
